import { useEffect, useState, useRef, useCallback } from 'react';
import { useActivity, useVirtualSession } from '@bizzabo/entities';
import getCountdown from './getCountdown';

export const useWaitingRoom = (
    eventUniqueName: string,
    activityId: number | string,
    withCountdown?: boolean
) => {
    activityId = +activityId;
    const { activity } = useActivity({ eventUniqueName, activityId });
    const { error, isLoading } = useVirtualSession({
        eventId: activity?.eventId,
        sessionId: activity?.id,
    });
    const countDownInterval = useRef(null);
    const polling = useRef(false);
    const [state, _setState] = useState({
        countdown: null,
        isActive: false,
    });
    const stateRef = useRef(state);

    const setState = newState => {
        stateRef.current = newState;
        _setState({ ...newState });
    };

    const handlePoll = useCallback(() => {
        const { formattedCountdown, started, finished } = getCountdown(
            activity?.startDateUTCTimestamp,
            activity?.waitingRoom?.startTime
        );
        const isActive = started && !finished;

        if (
            (withCountdown && formattedCountdown !== stateRef.current.countdown) ||
            isActive !== stateRef.current.isActive
        ) {
            setState({
                countdown: formattedCountdown,
                isActive,
            });
        }

        if (finished && countDownInterval.current) {
            clearInterval(countDownInterval.current);
        }
    }, [activity?.startDateUTCTimestamp, activity?.waitingRoom?.startTime]);

    useEffect(() => {
        if (
            activity?.waitingRoom?.enable &&
            new Date().getTime() < activity?.startDateUTCTimestamp &&
            !polling.current
        ) {
            polling.current = true;

            countDownInterval.current = setInterval(() => {
                handlePoll();
            }, 1000);
        }

        return () => {
            if (countDownInterval.current) {
                clearInterval(countDownInterval.current);
            }
        };
    }, [activity?.waitingRoom?.enable]);

    return {
        isActive: state.isActive && !error && !isLoading,
        countdown: state.countdown,
    };
};

export default useWaitingRoom;
